import type { CSSProperties } from 'react';

import type { LandingTheme } from '~/(tenant)/_lib/schema/landing-config.schema';
import { contrastRatio, deriveContrastingForeground } from '~/lib/utils/color-derivation';

export function isDarkColor(color: string) {
  const channels = [1, 3, 5].map((offset) => {
    const value = parseInt(color.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return channels[0]! * 0.2126 + channels[1]! * 0.7152 + channels[2]! * 0.0722 < 0.179;
}

export function contrastForeground(color: string) {
  const preferred = isDarkColor(color) ? '#ffffff' : '#171717';
  return contrastRatio(color, preferred) >= 4.5 ? preferred : deriveContrastingForeground(color);
}

export function landingTypographyStyle(fontFamily: LandingTheme['fontFamily']): CSSProperties & Record<`--${string}`, string | number> {
  if (fontFamily === 'instrument-serif') return {
    fontFamily: '"Landing Sans", "Landing Arabic", system-ui, sans-serif',
    '--landing-heading-font': '"Landing Instrument", "Landing Arabic", Georgia, serif',
    '--landing-heading-weight': 400,
  };
  if (fontFamily === 'orbitron') {
    return {
      fontFamily: '"Landing Plex", "Landing Plex Arabic", system-ui, sans-serif',
      '--landing-heading-font': '"Landing Orbitron", "Landing Arabic", system-ui, sans-serif',
      '--landing-heading-weight': 800,
      '--landing-control-font': '"Landing Chakra", "Landing Plex Arabic", system-ui, sans-serif',
      '--landing-control-weight': 700,
    } satisfies CSSProperties & Record<`--${string}`, string | number>;
  }
  if (fontFamily !== 'kinetica-aeonik') return {};
  return {
    fontFamily: '"Landing Aeonik", "Landing Arabic", system-ui, sans-serif',
    fontWeight: 400,
    '--landing-heading-font': '"Landing Kinetica", "Landing Arabic", system-ui, sans-serif',
    '--landing-control-font': '"Landing Kinetica", "Landing Arabic", system-ui, sans-serif',
    '--landing-control-weight': 700,
  } satisfies CSSProperties & Record<`--${string}`, string | number>;
}

export function landingThemeStyle(theme: LandingTheme = {}) {
  const dark = theme.bg ? isDarkColor(theme.bg) : theme.mode === 'dark';
  const background = theme.bg ?? (dark ? '#0a0a0a' : '#ffffff');
  const foreground = theme.ink ?? (theme.bg ? contrastForeground(theme.bg) : dark ? '#fafafa' : '#171717');
  const surface = theme.surface ?? (dark ? '#171717' : '#f5f5f5');
  const surfaceForeground = theme.surfaceForeground ?? contrastForeground(surface);
  const fonts = {
    'kinetica-aeonik': '"Landing Kinetica", "Landing Arabic", system-ui, sans-serif',
    montserrat: 'Montserrat, "Landing Arabic", system-ui, sans-serif',
    system: 'var(--font-sans-latin), var(--font-sans-arabic), system-ui, sans-serif',
    editorial: '"Landing Editorial", "Landing Arabic", Georgia, serif',
    myriad: '"Landing Myriad", var(--font-sans-arabic), system-ui, sans-serif',
    'dm-sans': '"Landing Sans", "Landing Arabic", system-ui, sans-serif',
    sora: '"Landing Sora", "Landing Arabic", system-ui, sans-serif',
    orbitron: '"Landing Orbitron", "Landing Arabic", system-ui, sans-serif',
    'instrument-serif': '"Landing Instrument", "Landing Arabic", Georgia, serif',
  };

  return {
    backgroundColor: background,
    color: foreground,
    '--background': background,
    '--foreground': foreground,
    '--landing-canvas': background,
    '--landing-ink': foreground,
    '--landing-surface': surface,
    '--landing-surface-foreground': surfaceForeground,
    '--card': surface,
    '--card-foreground': surfaceForeground,
    ...(theme.accent ? {
      '--primary': theme.accent,
      '--primary-foreground': theme.accentForeground ?? contrastForeground(theme.accent),
      '--ring': theme.accent,
    } : theme.accentForeground ? { '--primary-foreground': theme.accentForeground } : {}),
    ...(theme.secondary ? { '--landing-decoration': theme.secondary } : {}),
    ...(theme.fontFamily ? { fontFamily: fonts[theme.fontFamily === 'editorial' || theme.fontFamily === 'instrument-serif' ? 'dm-sans' : theme.fontFamily], '--landing-heading-font': fonts[theme.fontFamily] } : {}),
    ...landingTypographyStyle(theme.fontFamily),
    ...(theme.headingWeight ? { '--landing-heading-weight': { regular: 400, medium: 500, semibold: 600, bold: 700 }[theme.headingWeight] } : {}),
  } satisfies CSSProperties & Record<`--${string}`, string | number>;
}
